import React from 'react'
import styled from 'styled-components'
import StaticImage from '@components/images/StaticImg'
import { useRevealProps } from '../../MwPage/components/Reveal'
import { IDS } from '../../MwPage/content'
import LineIcon from '../../MwPage/icons'
import {
  Checks,
  cyan,
  darkRaise,
  Eyebrow,
  Hi,
  ink,
  Lede,
  muted,
  sans,
  Section,
  SectionHead,
  Small,
  Stack,
  teal,
} from '../../MwPage/styled'
import { useFocus } from '../focus'
import { Big, Card, Column, Grid, H2, Wrap } from '../styled'

// the prototype's .mphoto: a 4:3 crop with the face kept near the top
const Photo = styled.div`
  position: relative;
  overflow: hidden;
  aspect-ratio: 4 / 3;
  width: 100%;
  border-radius: 28px;
  background: ${darkRaise};

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center 20%;
  }
`

const Name = styled.div`
  h3 {
    margin: 0 0 6px;
    font: 800 clamp(1.4rem, 2.2vw, 1.7rem) / 1.2 ${sans};
    letter-spacing: -0.03em;
    color: ${ink};
  }

  p {
    margin: 0;
    color: ${muted};
    line-height: 1.6;
  }
`

const Stat = styled(Card)`
  padding: 14px 16px;

  span {
    display: block;
    margin-top: 4px;
    font-size: 13px;
    line-height: 1.4;
    color: ${muted};
  }
`

const Quote = styled.blockquote`
  margin: 0;
  padding: 2px 0 2px 16px;
  border-left: 3px solid ${cyan};
  font: 600 18px/1.5 ${sans};
  letter-spacing: -0.015em;
  color: ${ink};

  cite {
    display: block;
    margin-top: 6px;
    font: 700 13.5px/1.3 ${sans};
    color: ${teal};
  }
`

const TakeLine = styled.p`
  margin: 0;
  font-weight: 700;
  color: ${ink};
`

const RevealLede = ({ children }) => <Lede {...useRevealProps()}>{children}</Lede>

const RevealSmall = ({ children }) => (
  <Small style={{ fontSize: 15 }} {...useRevealProps()}>
    {children}
  </Small>
)

/** "Your mentor": Martin's photo, record, credentials and words. */
const MentorSection = () => {
  const { mentor } = useFocus()

  return (
    <Section id={IDS.mentor}>
      <Wrap>
        <Stack $gap="clamp(24px, 3vw, 32px)">
          <SectionHead>
            <Eyebrow {...useRevealProps()}>{mentor.eyebrow}</Eyebrow>
            <H2 {...useRevealProps()}>
              {mentor.headline} <Hi>{mentor.headlineAccent}</Hi>
            </H2>
            {mentor.lede && <RevealLede>{mentor.lede}</RevealLede>}
          </SectionHead>
          <Column $max={820} $gap={20}>
            <Photo {...useRevealProps()}>
              <StaticImage src={mentor.photo.src} alt={mentor.photo.alt} />
            </Photo>
            <Name {...useRevealProps()}>
              <h3>{mentor.name}</h3>
              <p>{mentor.bio}</p>
            </Name>
            <Grid $min={140} {...useRevealProps()}>
              {mentor.stats.map((stat) => (
                <Stat key={stat.figure} $tint>
                  <Big>{stat.figure}</Big>
                  <span>{stat.text}</span>
                </Stat>
              ))}
            </Grid>
            <Checks $column {...useRevealProps()}>
              {mentor.credentials.map(([text, title]) => (
                <li key={text}>
                  <LineIcon name="check" />
                  <span>
                    {text}
                    {title && <em>{title}</em>}
                  </span>
                </li>
              ))}
            </Checks>
            <Quote {...useRevealProps()}>
              {mentor.quote}
              <cite>{mentor.quoteName}</cite>
            </Quote>
            {mentor.interviews && <RevealSmall>{mentor.interviews}</RevealSmall>}
            <TakeLine {...useRevealProps()}>{mentor.take}</TakeLine>
          </Column>
        </Stack>
      </Wrap>
    </Section>
  )
}

export default MentorSection
